import type { LessonResult } from './player.js';

/** A result waiting to reach the server, with when it actually happened. */
export type QueuedResult = LessonResult & {
  readonly lessonId: string;
  /** ISO 8601, captured at the moment of the answer. */
  readonly at: string;
  /**
   * The session it belongs to.
   *
   * Carried so a result flushed after the teacher ended the lesson still knows
   * where it goes: without it the API has only the connection to go on, which
   * by then is the next lesson's, or none. A result with no session -- a
   * headset practising on its own -- is not queued at all (`add`).
   */
  readonly sessionId: string;
  /**
   * This result's own id, made when it is queued and the same on every send,
   * so the API stores a resend once (docs/05-session-protocol.md).
   */
  readonly resultId: string;
};

/**
 * A random (version 4) UUID. `crypto.randomUUID` exists only on a secure
 * page; the headset is served over https, but a laptop opening the dev server
 * by its LAN address is not, and a lesson must not fail over an id.
 */
export function newResultId(): string {
  const c = globalThis.crypto;
  if (typeof c.randomUUID === 'function') return c.randomUUID();
  const bytes = c.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * Somewhere durable to keep the queue. `localStorage` in the app; anything
 * with these two methods in tests.
 */
export interface QueueStorage {
  read(): string | null;
  write(value: string): void;
}

export type ResultQueueOptions = {
  now?: () => Date;
  /** Cap so a headset left running for a term cannot grow without limit. */
  maxEntries?: number;
};

/** Where the queue is kept; a school's data, cleared when the pairing ends (api/school-data.ts). */
export const RESULTS_KEY = 'vrlearning.results.v1';

/**
 * Holds lesson results until the server has them.
 *
 * Classroom Wi-Fi drops (docs/07-roadmap.md is explicit about designing for
 * it), so results are written down locally the moment they happen and sent
 * when the network allows. They are flushed with their original timestamps,
 * not the time of the flush (docs/05-session-protocol.md), because a teacher
 * looking at results wants to know when a student answered, not when the
 * access point came back.
 *
 * In M3 `flush` is wired to the WebSocket; the queue itself does not care what
 * transport takes the batch.
 */
export class ResultQueue {
  readonly #storage: QueueStorage;
  readonly #now: () => Date;
  readonly #maxEntries: number;
  #entries: QueuedResult[];
  #flushing: Promise<boolean> | undefined;
  /** A flush asked for while one was in flight: run once that one is done. */
  #again: ((batch: QueuedResult[]) => Promise<Iterable<string>>) | undefined;

  constructor(storage: QueueStorage, options: ResultQueueOptions = {}) {
    this.#storage = storage;
    this.#now = options.now ?? ((): Date => new Date());
    this.#maxEntries = options.maxEntries ?? 500;
    this.#entries = this.#load();
  }

  get pending(): readonly QueuedResult[] {
    return this.#entries;
  }

  /**
   * Writes a result down until the server has it -- a result of a class.
   *
   * One recorded practising alone (no session) is not kept: there is no
   * lesson on the server it belongs to, and the API files a result that
   * names no session under whichever lesson the headset is in when the queue
   * is flushed -- the teacher's next class, which never asked the question.
   */
  add(lessonId: string, result: LessonResult, sessionId: string | undefined): void {
    if (sessionId === undefined) return;
    this.#entries.push({
      ...result,
      lessonId,
      at: this.#now().toISOString(),
      sessionId,
      resultId: newResultId(),
    });

    // Oldest first: a full queue means something has been wrong for a long
    // time, and the recent answers are the ones the teacher is watching for.
    if (this.#entries.length > this.#maxEntries) {
      this.#entries = this.#entries.slice(-this.#maxEntries);
    }

    this.#save();
  }

  /**
   * Hands everything pending to `send`, which resolves with the ids of the
   * results the server answered for good -- stored, or dropped never to be
   * stored -- and only those are let go of: one the server refused no longer
   * holds back the ones stored beside it. Returns true when the whole batch
   * was answered. When `send` fails nothing is dropped, so the next attempt
   * retries; the ids make a resend of one that did get in harmless.
   */
  async flush(send: (batch: QueuedResult[]) => Promise<Iterable<string>>): Promise<boolean> {
    // A reconnect and a lesson ending can both ask to flush at once; without
    // this the same answers would be sent twice. The later ask is not lost:
    // what the first leaves unanswered -- a socket that dropped and came back
    // meanwhile -- is sent again straight after it.
    if (this.#flushing) {
      this.#again = send;
      return this.#flushing;
    }

    if (this.#entries.length === 0) return true;

    const batch = [...this.#entries];
    this.#flushing = (async () => {
      try {
        const answered = new Set(await send(batch));
        // Only what the server answered: anything recorded while the request
        // was in flight, or not answered, stays queued.
        this.#entries = this.#entries.filter((entry) => !answered.has(entry.resultId));
        this.#save();
        return batch.every((entry) => answered.has(entry.resultId));
      } catch {
        return false;
      } finally {
        this.#flushing = undefined;
        const again = this.#again;
        this.#again = undefined;
        if (again) void this.flush(again);
      }
    })();

    return this.#flushing;
  }

  clear(): void {
    this.#entries = [];
    this.#save();
  }

  #load(): QueuedResult[] {
    try {
      const raw = this.#storage.read();
      if (!raw) return [];
      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      let minted = false;
      const entries = parsed
        .filter(
          (entry): entry is QueuedResult =>
            typeof entry === 'object' &&
            entry !== null &&
            typeof (entry as QueuedResult).stepId === 'string' &&
            typeof (entry as QueuedResult).at === 'string' &&
            // Practice, from a version that queued it (see `add`).
            typeof (entry as QueuedResult).sessionId === 'string',
        )
        .map((entry) => {
          if (typeof (entry as { resultId?: unknown }).resultId === 'string') return entry;
          // Queued by a version from before result ids: given one now, and
          // written down at once (below), so it is the same on every send.
          minted = true;
          return { ...entry, resultId: newResultId() };
        });
      if (minted) {
        this.#entries = entries;
        this.#save();
      }
      return entries;
    } catch {
      // Corrupt or unreadable storage costs us the backlog, not the lesson.
      return [];
    }
  }

  #save(): void {
    try {
      this.#storage.write(JSON.stringify(this.#entries));
    } catch {
      // Full quota, or private browsing. The queue stays in memory and the
      // lesson carries on; a class in progress matters more than durability.
    }
  }
}

/** Backed by localStorage, which survives the headset browser being closed. */
export function browserQueueStorage(key = RESULTS_KEY): QueueStorage {
  return {
    read: () => localStorage.getItem(key),
    write: (value) => localStorage.setItem(key, value),
  };
}
